import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { resolveGroupCardTitle } from "utils/groupCardTitle";
import { trackMeta } from "components/pages/Educator/Dashboard/AssignmentGroupCard";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

const Title = styled.h2`
  color: ${colors.nightlyWoods500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: 2px solid ${colors.nightlyWoods950};
  border-radius: 100px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 48px;
  justify-content: center;
  padding: 0;
  width: 48px;
`;

const Footer = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
  justify-content: flex-end;
`;

const FooterButton = styled.button`
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  line-height: 26px;
  padding: 0 20px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const SecondaryButton = styled(FooterButton)`
  background: #fff;
  border: 1px solid ${colors.blue500};
  color: ${colors.blue500};
`;

const PrimaryButton = styled(FooterButton)`
  background: ${colors.blue500};
  border: none;
  color: ${colors.milkGlass500};
`;

const SaveError = styled.p`
  color: crimson;
  flex: 1;
  font-size: 14px;
  margin: 0;
`;

// ---- Group mini-card (Figma Group Card/V2 inside the modals) ----

const MiniCard = styled.div`
  border: 1px solid ${colors.blue400};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MiniHeader = styled.div`
  align-items: center;
  background: ${(p) => p.$bg};
  color: white;
  display: flex;
  gap: 8px;
  padding: 12px 16px;
`;

const MiniBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
`;

const TagRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const Tag = styled.span`
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
  border-radius: 11px;
  color: ${(p) => p.$text};
  font-size: 14px;
  line-height: 20px;
  padding: 2px 8px;
  white-space: nowrap;
`;

const CodeText = styled.span`
  color: ${colors.nightlyWoods500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 14px;
  letter-spacing: 0.5px;
  line-height: 20px;
  text-transform: uppercase;
`;

const CardName = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 26px;
`;

const SetLabel = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  line-height: 20px;
`;

// The modal's snapshot of the group being updated. `questionCount` overrides the
// stale bundle count with the in-page selection size. `setLabel` names what the
// count counts — the saved set by default, the filtered pool on the carousel path.
export const GroupMiniCard = ({ assignment, bundle, questionCount, setLabel = "My Question Set" }) => {
  const { code, title, trackType, showTrackTag } = resolveGroupCardTitle(assignment, bundle);
  const meta = trackMeta(trackType);
  return (
    <MiniCard>
      <MiniHeader $bg={meta.headerBg}>
        <span style={{ fontSize: 14, fontWeight: 700, lineHeight: "20px" }}>Group Codes:</span>
        <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.25px", lineHeight: "26px" }}>
          {assignment?.group_code?.toUpperCase()}
        </span>
      </MiniHeader>
      <MiniBody>
        <TagRow>
          {showTrackTag && (
            <Tag $bg={meta.tagBg} $border={meta.tagBorder} $text={meta.tagText}>{meta.label}</Tag>
          )}
          <CodeText>{code}</CodeText>
        </TagRow>
        <CardName>{title}</CardName>
        <SetLabel>{setLabel}{Number.isFinite(questionCount) ? ` (${questionCount})` : ""}</SetLabel>
      </MiniBody>
    </MiniCard>
  );
};

// ---- "Update My Question Set for:" (Figma 5136-76891) ----

export const UpdateQuestionSetModal = ({
  isOpen,
  onClose,
  assignment,
  bundle,
  questionCount,
  saving = false,
  error = null,
  onConfirm,
}) => (
  <Modal
    show={isOpen}
    onHide={onClose}
    centered
    dialogClassName="clm-modal-dialog"
    contentClassName="clm-modal-content"
    aria-label="Update My Question Set"
  >
    <Container>
      <Header>
        <Title>Update My Question Set for:</Title>
        <CloseButton aria-label="Close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" stroke={colors.nightlyWoods950} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </CloseButton>
      </Header>
      <GroupMiniCard assignment={assignment} bundle={bundle} questionCount={questionCount} />
      <Footer>
        {error && <SaveError role="alert">{error}</SaveError>}
        <SecondaryButton type="button" onClick={onClose} disabled={saving}>
          Back
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onConfirm} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </PrimaryButton>
      </Footer>
    </Container>
  </Modal>
);

// ---- "Select Groups for New Question Set" (Figma 5136-72581) ----

const Question = styled.p`
  color: ${colors.nightlyWoods500};
  font-size: 18px;
  letter-spacing: 0.25px;
  line-height: 26px;
  margin: 0;
`;

const GroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 32px;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const CheckboxLabel = styled.label`
  align-items: center;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  display: flex;
  font-size: 16px;
  gap: 8px;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
`;

const Box = styled.span`
  align-items: center;
  background: ${(p) => (p.$checked ? colors.green500 : "white")};
  border: 2px solid ${(p) => (p.$checked ? colors.green500 : colors.nightlyWoods500)};
  border-radius: 4px;
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  width: 24px;

  ${HiddenCheckbox}:focus-visible + label & {
    outline: 2px solid ${colors.blue500};
    outline-offset: 1px;
  }
`;

// Offered after a save when the bundle has other groups on the same topic. All
// codes come in pre-checked; Save applies the just-created set to whatever stays
// checked, Cancel skips the reuse entirely.
export const ReuseQuestionSetModal = ({
  isOpen,
  onClose,
  assignment,
  bundle,
  questionCount,
  setLabel,
  groupCodes = [],
  saving = false,
  error = null,
  onConfirm,
}) => {
  const [checked, setChecked] = useState(() => new Set(groupCodes));
  useEffect(() => {
    if (isOpen) setChecked(new Set(groupCodes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, groupCodes.join(",")]);

  const toggle = (code) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      dialogClassName="clm-modal-dialog"
      contentClassName="clm-modal-content"
      aria-label="Select Groups for New Question Set"
    >
      <Container>
        <Header>
          <Title>Select Groups for New Question Set</Title>
          <CloseButton aria-label="Close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" stroke={colors.nightlyWoods950} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </CloseButton>
        </Header>
        <GroupMiniCard assignment={assignment} bundle={bundle} questionCount={questionCount} setLabel={setLabel} />
        <Question>
          Group {assignment?.group_code?.toUpperCase()} is updated! Would you like the other groups assigned to this
          topic to use this same Question Set?
        </Question>
        <GroupList>
          {groupCodes.map((code) => {
            const inputId = `reuse-group-${code}`;
            const isChecked = checked.has(code);
            return (
              <div key={code}>
                <HiddenCheckbox id={inputId} type="checkbox" checked={isChecked} onChange={() => toggle(code)} />
                <CheckboxLabel htmlFor={inputId}>
                  <Box $checked={isChecked} aria-hidden>
                    {isChecked && <FontAwesomeIcon icon={faCheck} style={{ color: "white", fontSize: "14px" }} />}
                  </Box>
                  <span>{code.toUpperCase()}</span>
                </CheckboxLabel>
              </div>
            );
          })}
        </GroupList>
        <Footer>
          {error && <SaveError role="alert">{error}</SaveError>}
          <SecondaryButton type="button" onClick={onClose} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={() => onConfirm([...checked])} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </PrimaryButton>
        </Footer>
      </Container>
    </Modal>
  );
};
